/**
 * Currency formatting utilities for exchange rate management.
 *
 * Provides functions for formatting exchange rates, currency amounts,
 * and calculating conversions between currencies.
 */

/**
 * Currency symbols map
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  VND: '₫',
  CNY: '¥',
  KRW: '₩',
  SGD: 'S$',
  THB: '฿',
}

/**
 * Currency flag emoji map (for visual display)
 */
export const CURRENCY_FLAGS: Record<string, string> = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  JPY: '🇯🇵',
  VND: '🇻🇳',
  CNY: '🇨🇳',
  KRW: '🇰🇷',
  SGD: '🇸🇬',
  THB: '🇹🇭',
}

/**
 * Format a currency amount with symbol and proper decimals.
 *
 * @param amount - The amount to format
 * @param currency - Currency code (e.g., "USD", "VND")
 * @param options - Formatting options
 * @returns Formatted currency string
 *
 * @example
 * formatCurrencyAmount(23250, 'VND') // "₫23,250"
 * formatCurrencyAmount(1.5, 'USD') // "$1.50"
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  options?: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    compact?: boolean
  }
): string {
  const { minimumFractionDigits = 2, maximumFractionDigits = 2, compact = false } = options || {}

  // VND doesn't use decimal places
  const decimals = currency === 'VND' ? 0 : minimumFractionDigits
  const maxDecimals = currency === 'VND' ? 0 : maximumFractionDigits

  if (compact) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      minimumFractionDigits: decimals,
      maximumFractionDigits: maxDecimals,
    }).format(amount)
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: maxDecimals,
  }).format(amount)
}

/**
 * Format an exchange rate in the format: "1 USD = ₫23,250.00"
 *
 * @param currency - Currency code (e.g., "USD")
 * @param rateToVND - Exchange rate to VND
 * @returns Formatted exchange rate string
 *
 * @example
 * formatExchangeRate('USD', 23250) // "1 USD = ₫23,250"
 */
export function formatExchangeRate(currency: string, rateToVND: number): string {
  const formattedRate = formatCurrencyAmount(rateToVND, 'VND')
  return `1 ${currency} = ${formattedRate}`
}

/**
 * Convert an amount from one currency to another using exchange rate.
 *
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @param exchangeRates - Map of currency codes to VND rates
 * @returns Converted amount
 *
 * @example
 * convertCurrency(100, 'USD', 'VND', { USD: 23250 }) // 2325000
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number>
): number {
  // If converting from/to the same currency
  if (fromCurrency === toCurrency) {
    return amount
  }

  // If converting from VND
  if (fromCurrency === 'VND') {
    const toRate = exchangeRates[toCurrency]
    if (!toRate) throw new Error(`Exchange rate not found for ${toCurrency}`)
    return amount / toRate
  }

  // If converting to VND
  if (toCurrency === 'VND') {
    const fromRate = exchangeRates[fromCurrency]
    if (!fromRate) throw new Error(`Exchange rate not found for ${fromCurrency}`)
    return amount * fromRate
  }

  // Convert between two non-VND currencies (via VND)
  const fromRate = exchangeRates[fromCurrency]
  const toRate = exchangeRates[toCurrency]

  if (!fromRate) throw new Error(`Exchange rate not found for ${fromCurrency}`)
  if (!toRate) throw new Error(`Exchange rate not found for ${toCurrency}`)

  // Convert to VND first, then to target currency
  const vndAmount = amount * fromRate
  return vndAmount / toRate
}

/**
 * Calculate percentage change between two rates.
 *
 * @param oldRate - Previous rate
 * @param newRate - New rate
 * @returns Percentage change (positive = increase, negative = decrease)
 *
 * @example
 * calculateRateChange(23000, 23250) // 1.09 (1.09% increase)
 */
export function calculateRateChange(oldRate: number, newRate: number): number {
  if (oldRate === 0) return 0
  return ((newRate - oldRate) / oldRate) * 100
}

/**
 * Format percentage change with sign and symbol.
 *
 * @param change - Percentage change value
 * @returns Formatted percentage string
 *
 * @example
 * formatPercentageChange(1.09) // "+1.09%"
 * formatPercentageChange(-0.5) // "-0.50%"
 */
export function formatPercentageChange(change: number): string {
  const sign = change > 0 ? '+' : ''
  return `${sign}${change.toFixed(2)}%`
}

/**
 * Get currency symbol by currency code.
 *
 * @param currency - Currency code
 * @returns Currency symbol
 *
 * @example
 * getCurrencySymbol('USD') // "$"
 * getCurrencySymbol('VND') // "₫"
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] || currency
}

/**
 * Get currency flag emoji by currency code.
 *
 * @param currency - Currency code
 * @returns Flag emoji
 *
 * @example
 * getCurrencyFlag('USD') // "🇺🇸"
 * getCurrencyFlag('VND') // "🇻🇳"
 */
export function getCurrencyFlag(currency: string): string {
  return CURRENCY_FLAGS[currency] || ''
}

/**
 * Validate exchange rate value.
 *
 * @param rate - Rate to validate
 * @returns True if valid, false otherwise
 */
export function isValidExchangeRate(rate: number): boolean {
  return !isNaN(rate) && isFinite(rate) && rate > 0
}

/**
 * Format a conversion example for display.
 *
 * @param amount - Amount in VND
 * @param currency - Target currency code
 * @param rate - Exchange rate to VND
 * @returns Formatted conversion string
 *
 * @example
 * formatConversionExample(1000000, 'USD', 23250) // "₫1,000,000 = $43.01"
 */
export function formatConversionExample(
  amount: number,
  currency: string,
  rate: number
): string {
  const vndFormatted = formatCurrencyAmount(amount, 'VND')
  const converted = amount / rate
  const convertedFormatted = formatCurrencyAmount(converted, currency)
  return `${vndFormatted} = ${convertedFormatted}`
}
